const BERLIN_TZ = 'Europe/Berlin';

/** Offset (ms) of `timeZone` relative to UTC at the given instant. */
function tzOffsetMs(utcMs: number, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const map: Record<string, number> = {};
  for (const part of dtf.formatToParts(new Date(utcMs))) {
    if (part.type !== 'literal') map[part.type] = Number(part.value);
  }
  const asUtc = Date.UTC(
    map.year,
    map.month - 1,
    map.day,
    map.hour,
    map.minute,
    map.second,
  );
  return asUtc - utcMs;
}

/**
 * Target timestamp (ms) for a Sanity datetime, interpreted in Europe/Berlin.
 *
 * - Strings with an explicit timezone (`…Z` or `±hh:mm`) are an absolute instant
 *   and used as-is — identical to `new Date(date)`.
 * - Strings without a timezone have their wall-clock anchored to German time
 *   (CET/CEST, DST-aware), so the countdown targets the German moment.
 */
export function getCountdownTargetMs(date?: string): number | null {
  if (!date) return null;
  const clean = String(date).trim();
  if (!clean) return null;

  // Already carries a timezone → absolute instant.
  if (/([zZ])$|([+-]\d{2}:?\d{2})$/.test(clean)) {
    const t = new Date(clean).getTime();
    return Number.isNaN(t) ? null : t;
  }

  // No timezone → anchor the wall-clock to Berlin time.
  const m = clean.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (!m) {
    const t = new Date(clean).getTime();
    return Number.isNaN(t) ? null : t;
  }

  const [, y, mo, d, h = '00', mi = '00', s = '00'] = m;
  const wallUtc = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
  return wallUtc - tzOffsetMs(wallUtc, BERLIN_TZ);
}

export interface CountdownBreakdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const ZERO: CountdownBreakdown = { days: 0, hours: 0, minutes: 0, seconds: 0 };

/**
 * Remaining time broken into days/hours/minutes/seconds for a target instant.
 * Returns zeros when there is no target or it's already in the past. Use this
 * for the initial `useState` value so the countdown never flashes 00:00:00:00.
 */
export function getCountdownBreakdown(
  targetMs: number | null,
  nowMs: number = Date.now(),
): CountdownBreakdown {
  if (targetMs == null) return ZERO;
  const diff = targetMs - nowMs;
  if (diff <= 0) return ZERO;
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
    seconds: Math.floor((diff % 60_000) / 1_000),
  };
}
